// src/hooks/useNFTGallery.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { getMainnetProvider, getBaseProvider } from '../utils/providers';
import { getApiBaseUrl } from '../utils/apiConfig';

import { parseTxError } from '../utils/parseTxError';
import { launchGrandConfetti } from '../utils/launchConfetti';

// ---------------------------------------------------------------------------
// Additional badge collections rendered in “My NFT’s”
// ---------------------------------------------------------------------------
const BADGE_COLLECTIONS = [
  {
    name: 'Sogni AI OG Badge',
    contract: process.env.REACT_APP_BADGE_OG_CONTRACT,
    pathSlug: 'badge-og',
    singleImage: false
  },
  {
    name: 'Testnet Warrior – Relaxed',
    contract: process.env.REACT_APP_BADGE_WARRIOR_RELAXED_CONTRACT,
    pathSlug: 'badge-testnet-warrior-relaxed',
    singleImage: false
  },
  {
    name: 'Testnet Warrior – Fast',
    contract: process.env.REACT_APP_BADGE_WARRIOR_FAST_CONTRACT,
    pathSlug: 'badge-testnet-warrior-fast',
    singleImage: true
  },
  {
    name: 'Testnet Warrior – Artist',
    contract: process.env.REACT_APP_BADGE_WARRIOR_ARTIST_CONTRACT,
    pathSlug: 'badge-testnet-warrior-artist',
    singleImage: false
  }
];

const badgeAbi = [
  'function balanceOf(address owner) view returns (uint256)',
  'function tokenOfOwnerByIndex(address owner,uint256 index) view returns (uint256)'
];

const mintSupplyAbi = [
  'function getMintedCount() view returns (uint256)',
  'function maxSupply() view returns (uint256)'
];

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------
const GAS_LIMIT_BUFFER_PCT = 110; // +10 % safety margin
const EMPTY_CONFIRMED_MINTS = Object.freeze({});

async function getActiveMintAddress(contract, fallbackAccount) {
  if (contract?.signer && typeof contract.signer.getAddress === 'function') {
    try {
      return await contract.signer.getAddress();
    } catch (err) {
      console.warn('Failed to read active signer address for mint:', err);
    }
  }

  return fallbackAccount;
}


/**
 * Prepares EIP‑1559 fee overrides suitable for Base main‑net.
 * We only call provider.getFeeData() once per click, so it
 * does not add any noticeable latency.
 */
async function getBaseFeeOverrides(provider) {
  // pull the latest EIP-1559 data
  const { lastBaseFeePerGas } = await provider.getFeeData();
  console.log('lastBaseFeePerGas', lastBaseFeePerGas);

  // if for some reason the provider didn't return a base fee, fall back to no overrides
  if (!lastBaseFeePerGas) {
    return {};
  }

  // calculate a 10% “tip” on top of the base fee
  const maxPriorityFeePerGas = lastBaseFeePerGas.mul(10).div(100);

  // total ceiling = base fee + tip
  const maxFeePerGas = lastBaseFeePerGas.add(maxPriorityFeePerGas);

  return {
    maxFeePerGas,
    maxPriorityFeePerGas
  };
}

/* OLD code is so much more expensive:
- https://basescan.org/tx/0xc46b976796cbeb5476e7c992591c67b9de15ce01f13688ff060564e1971809be
- https://basescan.org/tx/0xdc2fc2df75aa58ae0140e539f9416990b9d71b9a58a1c447be0b0636ada8f46b

async function getBaseFeeOverrides(provider) {
  const fee = await provider.getFeeData();
  return {
    maxFeePerGas:        fee.maxFeePerGas,
    maxPriorityFeePerGas: fee.maxPriorityFeePerGas ??
      (fee.maxFeePerGas ? fee.maxFeePerGas.div(10) : undefined)
  };
}
*/
/**
 * Main gallery logic shared by Explorer / My NFT’s / Analytics
 */
export function useNFTGallery({
  baseURI,
  contract,
  publicContract,
  account,
  refreshGallery,
  confirmedAdminMints = EMPTY_CONFIRMED_MINTS,
  view,
  onRequireLogin
}) {
  // The live supply cap defines the selectable catalog. Environment values
  // only provide its initial value while the contract read is loading.
  const envTotalTokens = parseInt(process.env.REACT_APP_ACTUAL_MAX_SUPPLY || process.env.REACT_APP_INITIAL_MAX_SUPPLY || '1150', 10);
  const [mintLimit, setMintLimit] = useState(envTotalTokens);

  const mainnetProvider = getMainnetProvider();
  const provider = getBaseProvider();

  const navigate  = useNavigate();
  const location  = useLocation();
  const [searchParams] = useSearchParams();
  const { tokenId } = useParams();

  const [nfts,        setNfts]        = useState([]);
  // Effective id ceiling: env value, raised to cover any minted token above it.
  const [totalTokens, setTotalTokens] = useState(envTotalTokens);
  const [badges,      setBadges]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [showModal,   setShowModal]   = useState(false);

  const [searchQuery, setSearchQuery] = useState('ALL');
  const [networkFilter, setNetworkFilter] = useState('ALL'); // 'ALL', 'BASE', 'ETHERLINK'

  const [lastMinted,     setLastMinted]     = useState([]);
  const [lastTransfers,  setLastTransfers]  = useState([]);

  // Transfer state ----------------------------------------------------------
  const [transferAddress, setTransferAddress] = useState('');
  const [transferring,    setTransferring]    = useState(false);
  const [transferError,   setTransferError]   = useState(null);
  const [transferSuccess, setTransferSuccess] = useState(null);

  // Mint state --------------------------------------------------------------
  const [minting,      setMinting]      = useState(false);
  const [mintError,    setMintError]    = useState(null);
  const [mintSuccess,  setMintSuccess]  = useState(null);
  const [mintPrice,    setMintPrice]    = useState('0');
  const [isSoldOut,    setIsSoldOut]    = useState(false);
  const [remainingMints, setRemainingMints] = useState(null);

  // Turnstile
  const [turnstileToken, setTurnstileToken] = useState('');
  const handleTransferRef = useRef(null);
  const handleMintRef = useRef(null);

  const isWalletConnected = Boolean(account && contract);

  // -------------------------------------------------------------------------
  // Initial filter from URL
  // -------------------------------------------------------------------------
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && ['ALL', 'AVAILABLE', 'MINTED'].includes(filterParam)) {
      setSearchQuery(filterParam);
    }
  }, [searchParams]);

  // -------------------------------------------------------------------------
  // Worker NFTs
  // -------------------------------------------------------------------------
  const etherlinkContract = process.env.REACT_APP_ETHERLINK_NFT_CONTRACT;

  const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
  const isRealOwner = (owner) =>
    owner && owner.toLowerCase() !== ZERO_ADDRESS;

  const fetchNFTs = useCallback(async () => {
    setLoading(true);
    setRemainingMints(null);
    try {
      const baseUrl = getApiBaseUrl();

      // Fetch Base NFTs + optionally Etherlink NFTs in parallel
      const fetches = [axios.get(`${baseUrl}/nfts`)];
      if (etherlinkContract) {
        fetches.push(
          axios.get(`${baseUrl}/nfts?contract=${etherlinkContract}`)
            .catch(err => { console.error('Etherlink NFT fetch error:', err); return { data: {} }; })
        );
      }
      const [baseResponse, etherlinkResponse] = await Promise.all(fetches);

      const baseNftData = baseResponse.data || {};
      const etherlinkNftData = etherlinkResponse?.data || {};

      let catalogLimit = envTotalTokens;
      if (publicContract?.address) {
        try {
          const supplyContract = new ethers.Contract(
            publicContract.address,
            mintSupplyAbi,
            publicContract.provider || provider
          );
          const [mintedCount, maxSupply] = await Promise.all([
            supplyContract.getMintedCount(),
            supplyContract.maxSupply()
          ]);
          catalogLimit = maxSupply.toNumber();
          const soldOut = maxSupply.gt(0) && mintedCount.gte(maxSupply);
          setIsSoldOut(soldOut);
          setRemainingMints(soldOut ? 0 : maxSupply.sub(mintedCount).toNumber());
        } catch (supplyError) {
          console.error('Mint supply fetch error:', supplyError);
          setIsSoldOut(false);
        }
      } else {
        setIsSoldOut(false);
      }
      setMintLimit(catalogLimit);

      // Existing IDs can exceed the current cap; keep them visible without
      // offering new mints outside the selectable catalog.
      const highestMintedId = Math.max(
        0,
        ...Object.keys(confirmedAdminMints)
          .map(key => parseInt(key, 10))
          .filter(id => !Number.isNaN(id)),
        ...[baseNftData, etherlinkNftData].flatMap(data =>
          Object.keys(data || {})
            .map(key => parseInt(key, 10))
            .filter(id => !Number.isNaN(id) && isRealOwner(data[id]?.owner))
        )
      );
      const effectiveTotalTokens = Math.max(catalogLimit, highestMintedId);
      setTotalTokens(effectiveTotalTokens);

      // Build unified array — one entry per tokenId.
      // If token exists on Etherlink, it was bridged; Etherlink takes priority.
      // Skip zero-address owners (burned / stale index data).
      const arr = [];
      for (let i = 1; i <= effectiveTotalTokens; i++) {
        const baseEntry = baseNftData[i];
        const etherlinkEntry = etherlinkNftData[i];
        const confirmedAdminOwner = confirmedAdminMints[i];

        if (etherlinkEntry && isRealOwner(etherlinkEntry.owner)) {
          // Bridged to Etherlink — show as Etherlink
          arr.push({
            tokenId : i,
            mintId  : etherlinkEntry.mintId ?? i,
            minted  : true,
            owner   : etherlinkEntry.owner,
            chain   : 'Etherlink'
          });
        } else if (baseEntry && isRealOwner(baseEntry.owner)) {
          // Active on Base
          arr.push({
            tokenId : i,
            mintId  : baseEntry.mintId ?? i,
            minted  : true,
            owner   : baseEntry.owner,
            chain   : 'Base'
          });
        } else if (isRealOwner(confirmedAdminOwner)) {
          // A mint confirmed in this tab is authoritative even if the API
          // indexer has not caught up with the Transfer event yet.
          arr.push({
            tokenId : i,
            mintId  : i,
            minted  : true,
            owner   : confirmedAdminOwner,
            chain   : 'Base'
          });
        } else if (i <= catalogLimit) {
          arr.push({
            tokenId : i,
            mintId  : i,
            minted  : false,
            owner   : null,
            chain   : null
          });
        }
      }
      setNfts(arr);

      const [mintedR, transferR] = await Promise.all([
        axios.get(`${baseUrl}/nfts/lastMinted`),
        axios.get(`${baseUrl}/nfts/lastTransfers`)
      ]);
      setLastMinted(mintedR.data || []);
      setLastTransfers(transferR.data || []);

      // Support deep link: /nft/:tokenId even without explicit Route
      let deepId = tokenId ? parseInt(tokenId, 10) : null;
      if (!deepId && location?.pathname) {
        const match = location.pathname.match(/^\/nft\/(\d+)/i);
        if (match && match[1]) {
          const parsed = parseInt(match[1], 10);
          if (!Number.isNaN(parsed)) deepId = parsed;
        }
      }
      if (deepId) {
        const t = arr.find(x => x.tokenId === deepId);
        if (t) handleCardClick(t, { preventNavigate: true });
      }
    } catch (err) {
      console.error('Error fetching NFT data:', err);
    } finally {
      setLoading(false);
    }
  }, [tokenId, envTotalTokens, location?.pathname, publicContract, provider, confirmedAdminMints]);  // include pathname for deep links

  // -------------------------------------------------------------------------
  // Badge NFTs (wallet‑held)
  // -------------------------------------------------------------------------
  const fetchBadges = useCallback(async () => {
    if (!account) { setBadges([]); return; }

    try {
      const badgeProvider = getBaseProvider();
      const owned = [];

      for (const coll of BADGE_COLLECTIONS) {
        if (!coll.contract) continue;
        const c = new ethers.Contract(coll.contract, badgeAbi, badgeProvider);
        const bal = (await c.balanceOf(account)).toNumber();

        for (let i = 0; i < bal; i++) {
          let tokId = 1;
          try { tokId = (await c.tokenOfOwnerByIndex(account, i)).toNumber(); }
          catch (_) {/* non‑enumerable contracts default to 1 */}

          const pad   = coll.name === 'Sogni AI OG Badge'
            ? '001'
            : String(coll.singleImage ? 1 : tokId).padStart(3, '0');

          owned.push({
            tokenId:       tokId,
            collectionName: coll.name,
            contract:      coll.contract,
            smallUrl:      `https://nft.sogni.ai/smol/${coll.pathSlug}/${pad}.jpg`,
            bigUrl:        `https://nft.sogni.ai/big/${coll.pathSlug}/${pad}.jpg`
          });
        }
      }

      setBadges(owned);
    } catch (err) {
      console.error('Badge fetch error:', err);
      setBadges([]);
    }
  }, [account]);

  useEffect(() => { fetchNFTs(); }, [fetchNFTs, refreshGallery]);
  useEffect(() => { if (view === 'myNFTs') fetchBadges(); }, [view, fetchBadges]);

  // -------------------------------------------------------------------------
  // Mint price (on‑chain)
  // -------------------------------------------------------------------------
  useEffect(() => {
    (async () => {
      if (!publicContract) return;
      try {
        const p = await publicContract.mintPrice();
        setMintPrice(ethers.utils.formatEther(p));
      } catch (err) { console.error('Mint price error:', err); }
    })();
  }, [publicContract]);

  // -------------------------------------------------------------------------
  // Card click opens modal  (preventNavigate flag added)
  // -------------------------------------------------------------------------
  const handleCardClick = async (nft, options = {}) => {
    const { preventNavigate = false } = options;

    // The gallery is populated by the NFT API, which can lag behind a newly
    // confirmed mint (or simply remain stale in an already-open tab). Before
    // presenting an API-declared "unminted" token, ask the contract for the
    // authoritative owner. This prevents a real NFT from being shown as
    // unminted and incorrectly falling into the public mint-limit UI.
    let resolvedNFT = nft;
    if (!nft.minted && publicContract?.ownerOf) {
      try {
        const owner = await publicContract.ownerOf(nft.tokenId);
        if (isRealOwner(owner)) {
          resolvedNFT = {
            ...nft,
            minted: true,
            owner,
            chain: 'Base'
          };
          setNfts(prev => prev.map(item =>
            item.tokenId === nft.tokenId ? resolvedNFT : item
          ));
        }
      } catch (_) {
        // ownerOf reverts for a genuinely unminted token. In that normal case,
        // keep the API/gallery state and show the regular mint UI or limit note.
      }
    }

    setSelectedNFT(resolvedNFT);
    setShowModal(true);

    if (!preventNavigate) {
      navigate(`/nft/${resolvedNFT.tokenId}`);
    }

    if (resolvedNFT.minted) {
      try {
        // Only query Base contract for Base NFTs; Etherlink NFTs don't
        // exist on the Base contract so the call would return stale data.
        let rep = null;
        if (resolvedNFT.chain !== 'Etherlink' && publicContract) {
          rep = await publicContract.getReputationScore(resolvedNFT.tokenId);
        }

        let owner = resolvedNFT.owner;
        if (ethers.utils.isAddress(owner)) {
          try {
            const ens = await mainnetProvider.lookupAddress(owner);
            if (ens) owner = ens;
          } catch (_) {}
        }
        setSelectedNFT(prev => ({
          ...prev,
          ...(rep != null ? { reputation: rep.toString() } : {}),
          owner
        }));
      } catch (err) { console.error(err); }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedNFT(null);
    setTransferAddress('');
    setTransferError(null);
    setTransferSuccess(null);
    setMintError(null);
    setMintSuccess(null);
    setTurnstileToken('');
  };

  // -------------------------------------------------------------------------
  // Transfer logic
  // -------------------------------------------------------------------------
  const handleTransfer = async (resolvedAddress) => {
    setTransferError(null);
    setTransferSuccess(null);
    // Ensure we have a signer before entering transferring state
    if (!account || !contract) {
      if (onRequireLogin) onRequireLogin(async () => {
        // After successful reauth, call transfer again
        await handleTransferRef.current?.(resolvedAddress);
      });
      return;
    }
    setTransferring(true);
    try {

      // ---------- (1) estimate gas ----------
      const rawGas = await contract.estimateGas[
        'safeTransferFrom(address,address,uint256)'
      ](account, resolvedAddress, selectedNFT.tokenId);

      const gasLimit = rawGas.mul(GAS_LIMIT_BUFFER_PCT).div(100);

      // ---------- (2) get Base fee caps ----------
      const feeOverrides = await getBaseFeeOverrides(provider);

      // ---------- (3) submit tx ----------
      const tx = await contract['safeTransferFrom(address,address,uint256)'](
        account,
        resolvedAddress,
        selectedNFT.tokenId,
        {
          gasLimit,
          ...feeOverrides
        }
      );
      await tx.wait();

      launchGrandConfetti();
      setTransferSuccess(`You transferred NFT #${selectedNFT.tokenId}`);
      setTransferAddress('');
      setSelectedNFT(prev => ({ ...prev, owner: resolvedAddress }));
      setNfts(prev => prev.map(n =>
        n.tokenId === selectedNFT.tokenId ? { ...n, owner: resolvedAddress } : n
      ));
    } catch (err) {
      console.error(err);
      setTransferError(parseTxError(err));
    } finally { setTransferring(false); }
  };

  // -------------------------------------------------------------------------
  // Mint logic (with Turnstile)
  // -------------------------------------------------------------------------
  const handleMint = async () => {
    setMintError(null);
    setMintSuccess(null);

    // Validate tokenId is within the mint limit
    if (selectedNFT && selectedNFT.tokenId > mintLimit) {
      setMintError(`Token ID ${selectedNFT.tokenId} is above the current mint limit of ${mintLimit}. Minting is not yet available for this NFT.`);
      return;
    }

    if (!account || !contract) {
      if (onRequireLogin) {
        return onRequireLogin(async () => {
          // After reauth establishes signer/account, resume mint
          await handleMintRef.current?.();
        });
      }
      return;
    }
    try {
      setMinting(true);
      const mintAccount = await getActiveMintAddress(contract, account);
      const mintId = selectedNFT.tokenId;

      if (!mintAccount) {
        setMintError('Wallet not connected. Please reconnect and try again.');
        return;
      }

      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/getMintSignature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userAddress: mintAccount,
          tokenId: mintId,
          turnstileToken
        })
      });

      if (!res.ok) {
        let apiErr = `Mint signature error (status ${res.status})`;
        try {
          const errJson = await res.json();
          if (errJson && typeof errJson === 'object' && errJson.error) {
            const turnstileErrors = Array.isArray(errJson.details)
              ? errJson.details
              : [];

            if (errJson.error === 'Cloudflare Turnstile verification failed') {
              apiErr = turnstileErrors.includes('timeout-or-duplicate')
                ? 'Human verification expired or was already used. Please verify again.'
                : 'Human verification failed. Please verify again.';
            } else {
              apiErr = errJson.error;
            }
          }
        } catch (_) {/* fall back to generic error */}
        setMintError(apiErr);
        return; // abort the mint flow early
      }

      const { timestamp, signature } = await res.json();
      if (!timestamp || !signature) {
        setMintError('Mint signature response was incomplete. Please try again.');
        return;
      }

      const valueWei = ethers.utils.parseEther(mintPrice);

      // ---------- (1) gas estimate ----------
      const rawGas = await contract.estimateGas.mintWithSignature(
        mintId,
        timestamp,
        signature,
        { value: valueWei }
      );
      const gasLimit = rawGas.mul(GAS_LIMIT_BUFFER_PCT).div(100);

      // ---------- (2) fee caps ----------
      const feeOverrides = await getBaseFeeOverrides(provider);

      // ---------- (3) send tx ----------
      const tx = await contract.mintWithSignature(
        mintId,
        timestamp,
        signature,
        {
          value: valueWei,
          gasLimit,
          ...feeOverrides
        }
      );
      await tx.wait();

      launchGrandConfetti();
      setMintSuccess(`Congrats – you own NFT #${selectedNFT.tokenId}!`);
      setSelectedNFT(prev => ({ ...prev, minted: true, owner: mintAccount }));
      setNfts(prev => prev.map(n =>
        n.tokenId === selectedNFT.tokenId ? { ...n, minted: true, owner: mintAccount } : n
      ));
      await fetchNFTs();
    } catch (err) {
      console.error(err);
      setMintError(parseTxError(err));
    } finally {
      // Turnstile tokens expire and can only be validated once. Always clear
      // the submitted token so retries use a fresh challenge.
      setTurnstileToken('');
      setMinting(false);
    }
  };

  handleTransferRef.current = handleTransfer;
  handleMintRef.current = handleMint;

  // -------------------------------------------------------------------------
  // Filter worker NFTs for Explorer
  // -------------------------------------------------------------------------
  const filteredNFTs = nfts.filter(n => {
    // Network filter
    if (networkFilter === 'BASE' && n.chain !== 'Base') return false;
    if (networkFilter === 'ETHERLINK' && n.chain !== 'Etherlink') return false;

    const ua = account ? account.toLowerCase() : '';
    if (view === 'myNFTs') return n.minted && n.owner?.toLowerCase() === ua;
    if (isSoldOut && !n.minted) return false;
    if (searchQuery === 'ALL') return true;
    if (searchQuery === 'MINTED') return n.minted;
    if (searchQuery === 'AVAILABLE') return !n.minted;
    return true;
  });

  const truncateAddress = (addr) =>
    addr ? `${addr.slice(0,6)}…${addr.slice(-4)}` : '';

  return {
    loading,
    totalTokens,
    filteredNFTs,
    nfts,
    badges,
    lastMinted,
    lastTransfers,
    showModal,
    selectedNFT,
    searchQuery,
    setSearchQuery,
    networkFilter,
    setNetworkFilter,
    handleCardClick,
    handleModalClose,
    isWalletConnected,
    transferAddress,
    setTransferAddress,
    transferring,
    handleTransfer,
    transferError,
    transferSuccess,
    minting,
    handleMint,
    mintError,
    mintSuccess,
    truncateAddress,
    mainnetProvider,
    publicContract,
    baseURI,
    mintPrice,
    mintLimit,
    isSoldOut,
    remainingMints,
    turnstileToken,
    setTurnstileToken
  };
}
